import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Navigation, CalendarClock, History, HandCoins, MapPin } from 'lucide-react';
import { AgentLayout } from '../../components/AgentLayout';
import { AddMerchantSheet } from '../../components/AddMerchantSheet';
import api from '../../api';
import { encodeLan } from '../../api';
import { getGPS } from '../../gps';
import { haversineKm, fmtDistance, overdueTier, overdueTierStyle, relativePast, latestVisit, truncate } from '../../lib/agent-helpers';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';

function isOverdue(d) { return d && new Date(d) < new Date(new Date().toDateString()); }
function daysOverdue(d) {
  if (!d) return 0;
  const diff = new Date(new Date().toDateString()) - new Date(d);
  return Math.max(0, Math.floor(diff / 86400000));
}
function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); }
  catch { return d; }
}
function openMaps(lat, lng) {
  if (lat && lng) window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
}
function openMapsAddress(addr) {
  if (addr) window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`, '_blank');
}
function fmtINR(n) {
  const num = Number(n || 0);
  return `\u20B9${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
function bucketByDate(d) {
  if (!d) return null;
  const today = new Date(new Date().toDateString());
  const dt = new Date(new Date(d).toDateString());
  const diff = (dt - today) / 86400000;
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 7) return 'thisWeek';
  return null;
}
function bucketLoanCase(c) {
  // Cases without a follow-up date but with outstanding overdue should be treated as overdue
  if (!c.nextFollowupDate) {
    if ((c.loan?.totalOverdueAmount || 0) > 0) return 'overdue';
    return null;
  }
  return bucketByDate(c.nextFollowupDate);
}
function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`}>{msg}</div>;
}

export default function AgentFollowups() {
  const navigate = useNavigate();
  const [followups, setFollowups] = useState({ overdue: [], today: [], thisWeek: [] });
  const [loanFollowups, setLoanFollowups] = useState({ overdue: [], today: [], thisWeek: [] });
  const [activeTab, setActiveTab] = useState('overdue');
  const [typeFilter, setTypeFilter] = useState('all'); // all | merchants | loans
  const [searchQ, setSearchQ] = useState('');
  const [historyCard, setHistoryCard] = useState(null);
  const [rescheduleCard, setRescheduleCard] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleNote, setRescheduleNote] = useState('');
  const [showSheet, setShowSheet] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [toast, setToast] = useState({ show: false, msg: '' });
  const [loading, setLoading] = useState(true);

  const user = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();
  const recoveryEnabled = user.recoveryEnabled === true;
  const agentGps = getGPS();

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadData = async () => {
    try {
      const reqs = [api.get('/followups')];
      if (recoveryEnabled) reqs.push(api.get('/recovery/cases'));
      const results = await Promise.all(reqs);
      const r = results[0];
      setFollowups(r.data);
      if (recoveryEnabled && results[1]) {
        const buckets = { overdue: [], today: [], thisWeek: [] };
        for (const c of results[1].data || []) {
          const b = bucketLoanCase(c);
          if (b) buckets[b].push(c);
        }
        setLoanFollowups(buckets);
        const totalMerchant = (r.data.overdue?.length || 0) + (r.data.today?.length || 0);
        const totalLoan = buckets.overdue.length + buckets.today.length;
        if (totalMerchant === 0 && totalLoan > 0 && buckets.overdue.length === 0) setActiveTab('today');
        else if (r.data.overdue?.length === 0 && buckets.overdue.length === 0 && (r.data.today?.length > 0 || buckets.today.length > 0)) setActiveTab('today');
      } else if (r.data.overdue?.length === 0 && r.data.today?.length > 0) {
        setActiveTab('today');
      }
    } catch {} finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  const refreshHandler = useCallback(async () => { await loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { indicator: pullIndicator } = usePullToRefresh(refreshHandler);

  const handleLogOnly = async (merchantId) => {
    if (!rescheduleNote.trim()) { showToast('Add a note to log this visit'); return; }
    try {
      await api.post(`/merchants/${merchantId}/visit-log`, { note: rescheduleNote });
      showToast('Visit logged');
      setRescheduleCard(null);
      setRescheduleDate('');
      setRescheduleNote('');
      await loadData();
    } catch {
      showToast('Failed to log visit');
    }
  };

  const handleReschedule = async (merchantId) => {
    if (!rescheduleDate) return;
    try {
      await api.post(`/merchants/${merchantId}/visit-log`, {
        note: rescheduleNote || `Follow-up rescheduled to ${rescheduleDate}`,
        rescheduleDate
      });
      showToast('Follow-up rescheduled');
      setRescheduleCard(null);
      setRescheduleDate('');
      setRescheduleNote('');
      await loadData();
    } catch {
      showToast('Failed to reschedule');
    }
  };

  const handleEditSuccess = async () => {
    showToast('Merchant updated!');
    setEditingMerchant(null);
    setShowSheet(false);
    await loadData();
  };

  const openEditSheet = (merchant) => {
    setEditingMerchant(merchant);
    setShowSheet(true);
  };

  const tabs = [
    { key: 'overdue', label: 'Overdue', count: followups.overdue.length + (loanFollowups.overdue?.length || 0) },
    { key: 'today', label: 'Today', count: followups.today.length + (loanFollowups.today?.length || 0) },
    { key: 'thisWeek', label: 'This Week', count: followups.thisWeek.length + (loanFollowups.thisWeek?.length || 0) },
  ];

  const rawItems = followups[activeTab] || [];
  const rawLoanItems = loanFollowups[activeTab] || [];

  const q = searchQ.trim().toLowerCase();
  const matchesMerchant = (m) => !q || `${m.businessName || ''} ${m.ownerName || ''} ${m.phone || ''}`.toLowerCase().includes(q);
  const matchesLoan = (c) => !q || `${c.customerName || ''} ${c.clientId || ''} ${c.loanAccountNumber || ''} ${c.customerPhone || ''}`.toLowerCase().includes(q);

  const showMerchants = !recoveryEnabled || typeFilter !== 'loans';
  const showLoans = recoveryEnabled && typeFilter !== 'merchants';
  const items = showMerchants ? rawItems.filter(matchesMerchant) : [];
  const loanItems = showLoans ? rawLoanItems.filter(matchesLoan) : [];

  return (
    <AgentLayout
      overdueCount={followups.overdue?.length || 0}
      onAddClick={() => { setEditingMerchant(null); setShowSheet(true); }}
    >
      {pullIndicator}
      <div className="au-page">
        <div style={{ paddingTop: '14px' }}>
          <div className="sec-t" style={{ marginBottom: '14px' }}>Follow-ups</div>

          {/* Tabs */}
          <div className="fu-tabs-wrap">
            {tabs.map(tab => {
              const isOverdueTab = tab.key === 'overdue' && tab.count > 0;
              return (
                <button
                  key={tab.key}
                  className={`fu-tab ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`followup-tab-${tab.key}`}
                >
                  <span className="num" style={isOverdueTab ? { color: 'var(--red)' } : undefined}>{tab.count}</span>
                  <span className="lbl">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Search (always visible) + type filter (only when recovery workstream is on) */}
          <div className="fu-filter-bar" style={{ display: 'flex', gap: '8px', alignItems: 'center', margin: '12px 0 14px', flexWrap: 'wrap' }}>
            {recoveryEnabled && (
              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { k: 'all', label: 'All', count: rawItems.length + rawLoanItems.length },
                  { k: 'merchants', label: 'Merchants', count: rawItems.length },
                  { k: 'loans', label: 'Loans', count: rawLoanItems.length },
                ].map(opt => (
                  <button
                    key={opt.k}
                    onClick={() => setTypeFilter(opt.k)}
                    data-testid={`followup-type-${opt.k}`}
                    style={{
                      padding: '6px 12px', fontSize: '12px', fontWeight: 600,
                      borderRadius: '999px', cursor: 'pointer',
                      border: typeFilter === opt.k ? '1px solid var(--navy)' : '1px solid var(--g5,#e5e7eb)',
                      background: typeFilter === opt.k ? 'var(--navy)' : '#fff',
                      color: typeFilter === opt.k ? '#fff' : 'var(--g2,#374151)',
                    }}
                  >
                    {opt.label} <span style={{ opacity: 0.8 }}>({opt.count})</span>
                  </button>
                ))}
              </div>
            )}
            <input
              type="text"
              className="fi"
              placeholder="Search name or phone…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              data-testid="followup-search"
              style={{ flex: 1, minWidth: '160px', fontSize: '12px', padding: '6px 10px' }}
            />
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px', color: 'var(--g3)' }}>Loading…</div>
          ) : (items.length === 0 && loanItems.length === 0) ? (
            <div className="empty-state fu">
              <div className="empty-icon">{activeTab === 'overdue' ? '✅' : '📅'}</div>
              <div className="empty-title">
                {activeTab === 'overdue' ? 'No overdue follow-ups!' : activeTab === 'today' ? 'No follow-ups today' : 'No follow-ups this week'}
              </div>
            </div>
          ) : (
            <>
            {items.length > 0 && recoveryEnabled && (
              <div className="sec-hd" style={{ marginBottom: '8px' }}>
                <span className="sec-t" style={{ fontSize: '12px' }}>Merchants</span>
                <span className="sec-l">{items.length}</span>
              </div>
            )}
            {items.map((m, idx) => {
              const hasGPS = m.gps_lat && m.gps_lng;
              const showHistory = historyCard === m.id;
              const showReschedule = rescheduleCard === m.id;
              const dOver = daysOverdue(m.nextVisitDate);
              const tier = overdueTier(dOver);
              const distKm = (agentGps && hasGPS) ? haversineKm(agentGps, { lat: m.gps_lat, lng: m.gps_lng }) : null;
              const lv = latestVisit(m.visitLog);

              return (
                <div key={m.id} className={`mcard fu${idx % 5}`} data-testid={`followup-${m.id}`}>
                  <div className="mc-body">
                    <div className="mc-top">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="mc-nm">{m.businessName}</div>
                        <div className="mc-ow">{m.ownerName}</div>
                      </div>
                      {activeTab === 'overdue' ? (
                        <span style={{ ...overdueTierStyle(tier), padding: '3px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {dOver}d overdue
                        </span>
                      ) : (
                        <span className={`badge-${(m.status || 'new').toLowerCase()}`}>{m.status}</span>
                      )}
                    </div>
                    {lv && (
                      <div style={{ fontSize: '11px', color: 'var(--g3)', marginTop: '4px', display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                        <History size={11} style={{ marginTop: '1px', flexShrink: 0 }} />
                        <span><b style={{ color: 'var(--g2)' }}>Last visit {relativePast(lv.visitedAt || lv.addedAt)}:</b> {truncate(lv.note, 70) || '(no note)'}</span>
                      </div>
                    )}
                    <div className="mc-meta">
                      <a href={`tel:${m.phone}`} className="mc-mi clickable">
                        <Phone size={12} /> {m.phone}
                      </a>
                      <span className="mc-mi">📅 {formatDate(m.nextVisitDate)}</span>
                      {hasGPS && (
                        <span 
                          className="gps-ok clickable"
                          onClick={() => openMaps(m.gps_lat, m.gps_lng)}
                        >
                          <Navigation size={12} /> Map
                        </span>
                      )}
                      {distKm != null && (
                        <span className="mc-mi" style={{ color: 'var(--navy)', fontWeight: 600 }}>
                          📍 {fmtDistance(distKm)} away
                        </span>
                      )}
                    </div>
                    {m.services?.length > 0 && (
                      <div className="mc-tags">
                        {m.services.map(s => (
                          <span key={s.id} className={`stag ${s.slug === 'upi_qr' ? 'upi' : ''}`}>{s.name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Actions Row */}
                  <div className="mc-actions">
                    <button
                      className="mc-act-btn"
                      onClick={() => openEditSheet(m)}
                      data-testid={`edit-followup-${m.id}`}
                    >
                      Edit
                    </button>
                    <button
                      className="mc-act-btn"
                      style={{ flex: 1 }}
                      onClick={() => { setRescheduleCard(showReschedule ? null : m.id); setRescheduleDate(''); setRescheduleNote(''); }}
                      data-testid={`reschedule-followup-${m.id}`}
                    >
                      <CalendarClock size={12} /> Reschedule
                    </button>
                    <button
                      className="mc-act-btn"
                      onClick={() => setHistoryCard(showHistory ? null : m.id)}
                    >
                      <History size={12} /> History
                    </button>
                  </div>
                  {/* Reschedule Inline */}
                  {showReschedule && (
                    <div className="mc-reschedule">
                      <div className="mc-reschedule-title">Reschedule Follow-up</div>
                      <textarea className="fi" rows={2} placeholder="Add notes about this visit..."
                        value={rescheduleNote} onChange={e => setRescheduleNote(e.target.value)}
                        style={{ marginBottom: '8px', fontSize: '12px' }} />
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--g3)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CalendarClock size={11} /> Next visit date
                      </div>
                      <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        {[
                          { label: 'Tomorrow', days: 1 },
                          { label: 'In 3 days', days: 3 },
                          { label: 'Next week', days: 7 },
                        ].map(p => {
                          const d = new Date(); d.setDate(d.getDate() + p.days);
                          const iso = d.toISOString().slice(0, 10);
                          const active = rescheduleDate === iso;
                          return (
                            <button key={p.label} type="button" onClick={() => setRescheduleDate(iso)}
                              style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '999px', cursor: 'pointer',
                                border: active ? '1px solid var(--navy)' : '1px solid var(--g2)',
                                background: active ? 'var(--navy)' : '#fff',
                                color: active ? '#fff' : 'var(--navy)', fontWeight: 600 }}>{p.label}</button>
                          );
                        })}
                      </div>
                      <div className="mc-reschedule-row">
                        <input type="date" className="fi" value={rescheduleDate}
                          min={new Date().toISOString().slice(0,10)}
                          onChange={e => setRescheduleDate(e.target.value)} />
                        <button className="btn-navy" onClick={() => handleReschedule(m.id)}
                          disabled={!rescheduleDate}>Save</button>
                      </div>
                      <div style={{ marginTop: '6px', textAlign: 'right' }}>
                        <button type="button" onClick={() => handleLogOnly(m.id)}
                          disabled={!rescheduleNote.trim()}
                          style={{ background: 'none', border: 'none', color: 'var(--navy)', fontSize: '11px', fontWeight: 600, cursor: rescheduleNote.trim() ? 'pointer' : 'not-allowed', opacity: rescheduleNote.trim() ? 1 : 0.45, textDecoration: 'underline' }}>
                          + Log note only (don't reschedule)
                        </button>
                      </div>
                    </div>
                  )}
                  {/* History Panel */}
                  {showHistory && (
                    <div className="mc-history">
                      <div className="mc-hist-title">Visit History ({m.visitLog?.length || 0})</div>
                      {(!m.visitLog || m.visitLog.length === 0) ? (
                        <div className="mc-hist-empty">No visits recorded yet</div>
                      ) : (
                        m.visitLog.slice(-3).reverse().map((v, i) => (
                          <div key={i} className="mc-hist-item">
                            <div className="mc-hist-date">{new Date(v.visitedAt || v.addedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                            <div className="mc-hist-note">{v.note}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {loanItems.length > 0 && (
              <div className="sec-hd" style={{ marginBottom: '8px', marginTop: items.length > 0 ? '16px' : '0' }}>
                <span className="sec-t" style={{ fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <HandCoins size={14} /> Loan Recovery
                </span>
                <span className="sec-l">{loanItems.length}</span>
              </div>
            )}
            {loanItems.map((c, idx) => {
              const overdue = activeTab === 'overdue';
              const phone = c.customerPhone;
              const addr = c.customerAddress;
              const gps = c.lastVisitGps;
              const hasPinned = !!(gps && gps.lat && gps.lng);
              return (
                <div key={c.id || c.loanAccountNumber} className={`mcard fu${idx % 5}`}>
                  <div className="mc-body">
                    <div className="mc-top">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="mc-nm">{c.customerName || c.clientId}</div>
                        <div className="mc-ow">Loan {c.loanAccountNumber}</div>
                      </div>
                      {overdue ? (
                        <span className="badge-overdue">{daysOverdue(c.nextFollowupDate)}d overdue</span>
                      ) : (
                        <span className="status-pill badge-today" style={{ fontSize: '10px' }}>
                          {fmtINR(c.loan?.totalOverdueAmount)}
                        </span>
                      )}
                    </div>
                    <div className="mc-meta">
                      {phone && (
                        <a href={`tel:${phone}`} className="mc-mi clickable">
                          <Phone size={12} /> {phone}
                        </a>
                      )}
                      <span className="mc-mi">📅 {formatDate(c.nextFollowupDate)}</span>
                      {hasPinned ? (
                        <span className="mc-mi clickable" onClick={() => openMaps(gps.lat, gps.lng)} style={{ color: 'var(--navy)', fontWeight: 600 }}>
                          <MapPin size={12} /> Pinned location
                        </span>
                      ) : addr ? (
                        <span className="mc-mi clickable" onClick={() => openMapsAddress(addr)}>
                          <MapPin size={12} /> {addr.length > 28 ? addr.slice(0, 28) + '…' : addr}
                        </span>
                      ) : null}
                    </div>
                    {!overdue && (
                      <div className="mc-meta" style={{ marginTop: '4px' }}>
                        <span className="mc-mi" style={{ color: 'var(--red)', fontWeight: 600 }}>
                          Overdue {fmtINR(c.loan?.totalOverdueAmount)} · {c.loan?.dpdDays ?? 0}d DPD
                        </span>
                      </div>
                    )}
                    {!hasPinned && (
                      <div className="mc-meta" style={{ marginTop: '4px' }}>
                        <span className="mc-mi" style={{ color: 'var(--g3)', fontSize: '10.5px' }}>
                          No pin yet · GPS will be saved on first visit
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mc-actions">
                    {hasPinned ? (
                      <button className="mc-act-btn" style={{ flex: 1 }} onClick={() => openMaps(gps.lat, gps.lng)}>
                        <Navigation size={12} /> Navigate
                      </button>
                    ) : addr ? (
                      <button className="mc-act-btn" style={{ flex: 1 }} onClick={() => openMapsAddress(addr)}>
                        <Navigation size={12} /> Navigate
                      </button>
                    ) : null}
                    <button className="mc-act-btn" style={{ flex: 1 }} onClick={() => navigate(`/recovery/cases/${encodeLan(c.loanAccountNumber)}`)}>
                      Open Case
                    </button>
                  </div>
                </div>
              );
            })}
            </>
          )}
        </div>
        <div style={{ height: '20px' }} />
      </div>

      <AddMerchantSheet
        isOpen={showSheet}
        onClose={() => { setShowSheet(false); setEditingMerchant(null); }}
        onSuccess={handleEditSuccess}
        editMerchant={editingMerchant}
      />

      <Toast msg={toast.msg} show={toast.show} />
    </AgentLayout>
  );
}
